import React, { useState, useEffect } from 'react';
import {
    Card, Table, Input, Space, Typography, Tag, Row, Col, DatePicker, Select, Statistic, Button
} from 'antd';
import {
    SearchOutlined, DollarOutlined, ShoppingCartOutlined, FileTextOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const SalesList = () => {
    const { t } = useTranslation(['pharmacy', 'common']);
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [currencyFilter, setCurrencyFilter] = useState('');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

    useEffect(() => {
        fetchSales();
    }, [dateRange, statusFilter, currencyFilter]);

    const fetchSales = async (page = 1) => {
        try {
            setLoading(true);
            let url = `/pharmacy/sales?page=${page}&limit=${pagination.pageSize}`;

            if (dateRange && dateRange[0] && dateRange[1]) {
                url += `&startDate=${dateRange[0].format('YYYY-MM-DD')}&endDate=${dateRange[1].format('YYYY-MM-DD')}`;
            }
            if (statusFilter) url += `&paymentStatus=${statusFilter}`;
            if (currencyFilter) url += `&currency=${currencyFilter}`;

            const res = await api.get(url);
            const data = res.data;

            setSales(data.data || []);
            setPagination(prev => ({
                ...prev,
                current: page,
                total: data.total || data.count || 0
            }));
        } catch (error) {
            console.error('Error fetching sales:', error);
        } finally {
            setLoading(false);
        }
    };

    // Filter locally by search
    const filteredSales = search
        ? sales.filter(s =>
            (s.saleNumber || '').toLowerCase().includes(search.toLowerCase()) ||
            (s.patientId?.name || '').toLowerCase().includes(search.toLowerCase()) ||
            (s.patientId?.patientId || '').toLowerCase().includes(search.toLowerCase())
        )
        : sales;

    // Stats
    const totalRevenue = filteredSales.reduce((sum, s) => sum + (s.totalAmount || 0), 0);
    const totalPaid = filteredSales.reduce((sum, s) => sum + (s.amountPaid || 0), 0);

    const columns = [
        {
            title: t('pharmacy:salesList.invoiceNumber'),
            dataIndex: 'saleNumber',
            key: 'saleNumber',
            render: (text) => <Text strong style={{ color: '#1890ff' }}>{text}</Text>,
            width: 140
        },
        {
            title: t('pharmacy:common.date'),
            dataIndex: 'saleDate',
            key: 'saleDate',
            render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
            width: 160,
            sorter: (a, b) => new Date(a.saleDate) - new Date(b.saleDate)
        },
        {
            title: t('pharmacy:common.patient'),
            dataIndex: 'patientId',
            key: 'patient',
            render: (p) => p?.name || <Text type="secondary">{t('pharmacy:common.walkIn')}</Text>,
            width: 150
        },
        {
            title: t('pharmacy:common.items'),
            dataIndex: 'items',
            key: 'items',
            render: (items) => items?.length || 0,
            width: 70,
            align: 'center'
        },
        {
            title: t('pharmacy:common.currency'),
            dataIndex: 'currency',
            key: 'currency',
            width: 80,
            render: (c) => <Tag>{c}</Tag>
        },
        {
            title: t('pharmacy:common.total'),
            dataIndex: 'totalAmount',
            key: 'total',
            width: 130,
            render: (amount, record) => (
                <Text strong>
                    {record.currency === 'IQD' ? `IQD ${amount?.toLocaleString()}` : `$${amount?.toFixed(2)}`}
                </Text>
            ),
            sorter: (a, b) => a.totalAmount - b.totalAmount
        },
        {
            title: t('pharmacy:salesList.paid'),
            dataIndex: 'amountPaid',
            key: 'amountPaid',
            width: 130,
            render: (amount, record) => (
                record.currency === 'IQD' ? `IQD ${amount?.toLocaleString()}` : `$${amount?.toFixed(2)}`
            )
        },
        {
            title: t('pharmacy:common.status'),
            dataIndex: 'paymentStatus',
            key: 'status',
            width: 100,
            render: (status) => (
                <Tag color={status === 'Paid' ? 'green' : status === 'Partial' ? 'orange' : status === 'Refunded' ? 'blue' : 'red'}>
                    {t(`pharmacy:common.paymentStatuses.${status}`, status)}
                </Tag>
            )
        },
        {
            title: t('pharmacy:common.payment'),
            dataIndex: 'paymentMethod',
            key: 'payment',
            width: 110
        },
        {
            title: t('pharmacy:salesList.soldBy'),
            dataIndex: 'pharmacyUserId',
            key: 'soldBy',
            render: (u) => u?.name || '-',
            width: 110
        }
    ];

    const expandedRowRender = (record) => (
        <Table
            dataSource={record.items}
            rowKey={(item, idx) => `${item.pharmacyItemId}-${idx}`}
            pagination={false}
            size="small"
            columns={[
                { title: t('pharmacy:common.item'), dataIndex: 'itemName', key: 'itemName' },
                { title: t('pharmacy:common.qty'), dataIndex: 'quantity', key: 'quantity', width: 60 },
                {
                    title: t('pharmacy:common.unitPrice'), dataIndex: 'unitPrice', key: 'unitPrice', width: 110,
                    render: (v) => record.currency === 'IQD' ? `IQD ${v?.toLocaleString()}` : `$${v?.toFixed(2)}`
                },
                {
                    title: t('pharmacy:common.discount'), dataIndex: 'discount', key: 'discount', width: 90,
                    render: (v) => record.currency === 'IQD' ? `IQD ${(v || 0).toLocaleString()}` : `$${(v || 0).toFixed(2)}`
                },
                {
                    title: t('pharmacy:common.total'), dataIndex: 'total', key: 'total', width: 110,
                    render: (v) => <Text strong>{record.currency === 'IQD' ? `IQD ${v?.toLocaleString()}` : `$${v?.toFixed(2)}`}</Text>
                }
            ]}
        />
    );

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}><FileTextOutlined /> {t('pharmacy:salesList.title')}</Title>

            {/* Stats */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={8} lg={6}>
                    <Card size="small">
                        <Statistic
                            title={t('pharmacy:salesList.totalSales')}
                            value={filteredSales.length}
                            prefix={<ShoppingCartOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} lg={6}>
                    <Card size="small">
                        <Statistic
                            title={t('pharmacy:salesList.totalRevenue')}
                            value={totalRevenue}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                            precision={2}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} lg={6}>
                    <Card size="small">
                        <Statistic
                            title={t('pharmacy:salesList.totalCollected')}
                            value={totalPaid}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                            precision={2}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filters */}
            <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={[12, 12]} align="middle">
                    <Col xs={24} sm={8} lg={6}>
                        <Input
                            placeholder={t('pharmacy:salesList.searchPlaceholder')}
                            prefix={<SearchOutlined />}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={8} lg={6}>
                        <RangePicker
                            style={{ width: '100%' }}
                            onChange={(dates) => setDateRange(dates)}
                            value={dateRange}
                        />
                    </Col>
                    <Col xs={12} sm={4} lg={3}>
                        <Select
                            style={{ width: '100%' }}
                            placeholder={t('pharmacy:common.status')}
                            value={statusFilter || undefined}
                            onChange={(val) => setStatusFilter(val || '')}
                            allowClear
                        >
                            <Select.Option value="Paid">{t('pharmacy:common.paymentStatuses.Paid')}</Select.Option>
                            <Select.Option value="Partial">{t('pharmacy:common.paymentStatuses.Partial')}</Select.Option>
                            <Select.Option value="Pending">{t('pharmacy:common.paymentStatuses.Pending')}</Select.Option>
                            <Select.Option value="Refunded">{t('pharmacy:common.paymentStatuses.Refunded')}</Select.Option>
                        </Select>
                    </Col>
                    <Col xs={12} sm={4} lg={3}>
                        <Select
                            style={{ width: '100%' }}
                            placeholder={t('pharmacy:common.currency')}
                            value={currencyFilter || undefined}
                            onChange={(val) => setCurrencyFilter(val || '')}
                            allowClear
                        >
                            <Select.Option value="IQD">IQD</Select.Option>
                            <Select.Option value="USD">USD</Select.Option>
                        </Select>
                    </Col>
                </Row>
            </Card>

            {/* Sales Table */}
            <Card>
                <Table
                    dataSource={filteredSales}
                    columns={columns}
                    rowKey="_id"
                    loading={loading}
                    size="small"
                    expandable={{ expandedRowRender }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: false,
                        showTotal: (total) => t('pharmacy:salesList.totalCount', { total }),
                        onChange: (page) => fetchSales(page)
                    }}
                    scroll={{ x: 1100 }}
                />
            </Card>
        </div>
    );
};

export default SalesList;
